// src/app/[locale]/contact/ContactView.tsx
"use client";
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Send, CheckCircle, AlertCircle, Loader2, X } from "lucide-react";
import { useTranslations } from "next-intl";
import ModernNavBar from "@/components/ModernNavBar";
import { useContactActions } from "@/hooks/useContactActions";
import { handleError } from "@/utils/errorHandler";
import { ContactUserInfo as UserInfo } from "@/server-side/types/user";

interface ContactViewProps {
  userInfo: UserInfo | null;
}

const ContactView: React.FC<ContactViewProps> = ({ userInfo }) => {
  const t = useTranslations("Contact");
  const { submitContactForm } = useContactActions();
  const [formData, setFormData] = useState({
    email: userInfo?.email || "",
    message: "",
  });
  const [errors, setErrors] = useState({
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});
  const [error, setError] = useState<string | null>(null);

  const validateForm = () => {
    const newErrors = {
      email: "",
      message: "",
    };

    // Email validation
    if (!formData.email) {
      newErrors.email = t("Errors.EmailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("Errors.InvalidEmail");
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = t("Errors.MessageRequired");
    } else if (formData.message.length < 3) {
      newErrors.message = t("Errors.MessageTooShort");
    }

    setErrors(newErrors);
    return !newErrors.email && !newErrors.message;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user types
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    
    // Clear error when user makes changes
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({});
    setError(null);

    await submitContactForm(formData, {
      onSuccess: () => {
        setSubmitStatus({
          success: true,
          message: t("SuccessMessage"),
        });
        
        // Reset form on success
        setFormData({
          email: userInfo?.email || "", // Preserve the email if user is logged in
          message: "",
        });
        setIsSubmitting(false);
      },
      onError: (err) => {
        let userMessage = t("Errors.UnexpectedError");
        handleError(err, { component: "ContactView", action: "submitContactForm" }, setError, userMessage);
        setIsSubmitting(false);
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
      <main className="container mx-auto px-4 py-8 pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          {/* Page Header */}
          <div className="text-center mb-10">
            <div className="inline-block p-3 bg-blue-500/20 rounded-full mb-4">
              <Mail className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-4">
              {t("Title")}
            </h1>
            <p className="text-gray-400 max-w-md mx-auto">
              {t("Description")}
            </p>
          </div>

          {/* Status Message */}
          {submitStatus.message && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-6 p-4 rounded-lg border ${
                submitStatus.success
                  ? "bg-green-500/10 border-green-500/30 text-green-400"
                  : "bg-red-500/10 border-red-500/30 text-red-400"
              }`}
            >
              <div className="flex items-start gap-3">
                {submitStatus.success ? (
                  <CheckCircle className="w-5 h-5 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 mt-0.5" />
                )}
                <p>{submitStatus.message}</p>
              </div>
            </motion.div>
          )}

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-400">{error}</p>
                </div>
                <button 
                  onClick={() => setError(null)}
                  className="text-red-400 hover:text-red-300 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gray-800/60 rounded-xl p-6 border border-gray-700/70 shadow-xl"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Input */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  {t("Form.EmailLabel")}
                </label>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-gray-700/50 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-colors ${
                      errors.email
                        ? "border-red-500/50"
                        : "border-gray-600/50 focus:border-blue-500/50"
                    }`}
                    placeholder={t("Form.EmailPlaceholder")}
                    disabled={isSubmitting}
                  />
                  <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
                {errors.email && (
                  <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Message Textarea */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  {t("Form.MessageLabel")}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className={`w-full px-4 py-3 bg-gray-700/50 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-colors ${
                    errors.message
                      ? "border-red-500/50"
                      : "border-gray-600/50 focus:border-blue-500/50"
                  }`}
                  placeholder={t("Form.MessagePlaceholder")}
                  disabled={isSubmitting}
                ></textarea>
                {errors.message && (
                  <p className="text-red-400 text-sm mt-1">{errors.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t("Form.Submitting")}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {t("Form.SubmitButton")}
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Additional Contact Info */}
          <div className="mt-8 text-center">
            <p className="text-gray-400">
              {t("ContactInfo").split("support@atlivo.com")[0]}
              <a
                href="mailto:support@atlivo.com"
                className="text-blue-400 hover:text-blue-300 transition-colors"
              >
                support@atlivo.com
              </a>
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default ContactView;